/**
 * Client-side parser for unfixable domain rules Excel (sheet 修正不可内容).
 * Validation rules mirror backend/backend_api/guidelines/unfixable_rules_parser.py
 */

import * as XLSX from 'xlsx';
import {
    decodeDomainForDisplay,
    encodeDomainForStorage,
} from './urlDisplay';

export const UNFIXABLE_RULES_SHEET_NAME = '修正不可内容';
export const UNFIXABLE_COL_ASP = 'ASP';
export const UNFIXABLE_COL_OPERATION_COMPANY = '運営会社';
export const UNFIXABLE_COL_MEDIA = '媒体';
export const UNFIXABLE_COL_DOMAIN = 'ドメイン';
export const UNFIXABLE_COL_DESCRIPTION = '修正不可内容';

export const UNFIXABLE_REQUIRED_HEADERS = [
    UNFIXABLE_COL_ASP,
    UNFIXABLE_COL_OPERATION_COMPANY,
    UNFIXABLE_COL_MEDIA,
    UNFIXABLE_COL_DOMAIN,
    UNFIXABLE_COL_DESCRIPTION,
] as const;

const MEDIA_HEADER_ALIASES = new Set([UNFIXABLE_COL_MEDIA, '媒体名']);
const DOMAIN_HEADER_ALIASES = new Set([UNFIXABLE_COL_DOMAIN, 'ドメイン名']);

/** Safe download filename: {guidelineName}_修正不可内容_v{version}.xlsx */
export function buildUnfixableRulesExportFilename(
    guidelineName: string,
    version?: number | null
): string {
    const safeName =
        (guidelineName || 'guideline')
            .trim()
            .replace(/[\\/:*?"<>|]/g, '_')
            .replace(/\s+/g, '_')
            .slice(0, 80) || 'guideline';
    const versionPart = version != null ? `_v${version}` : '';
    return `${safeName}_修正不可内容${versionPart}.xlsx`;
}

export interface UnfixableDomainRuleRow {
    id: string;
    domain: string;
    description: string;
    asp: string;
    operation_company: string;
    media_name: string;
    sourceRow?: number;
}

/** Keep the same row order as the uploaded Excel when source_row is set. */
export function sortUnfixableRulesBySourceRow<T extends { source_row?: number | null; sourceRow?: number | null }>(
    rules: T[]
): T[] {
    if (!rules.length) return rules;
    const rowNum = (r: T) => r.source_row ?? r.sourceRow ?? null;
    if (!rules.every((r) => rowNum(r) != null && rowNum(r)! > 0)) {
        return rules;
    }
    return [...rules].sort((a, b) => (rowNum(a) as number) - (rowNum(b) as number));
}

export class UnfixableRulesParseError extends Error {
    row?: number;

    constructor(message: string, row?: number) {
        super(message);
        this.name = 'UnfixableRulesParseError';
        this.row = row;
    }
}

function cellStr(value: unknown): string {
    if (value == null) return '';
    return String(value).replace(/\u3000/g, ' ').trim();
}

export function normalizeDomain(domain: string, stripWww = true): string {
    let s = encodeDomainForStorage((domain || '').trim());
    s = s.replace(/^[\s\u3000]+|[\s\u3000]+$/g, '');
    if (stripWww && s.startsWith('www.')) {
        s = s.slice(4);
    }
    return s;
}

/** Convert URL / host / bare domain cell value to normalized domain. */
export function domainInputToDomain(raw: string, stripWww = true): string {
    const s = cellStr(raw);
    if (!s) return '';

    let host: string | null = null;

    try {
        if (/^https?:\/\//i.test(s) || s.startsWith('//')) {
            const url = new URL(s.startsWith('//') ? `https:${s}` : s);
            host = url.hostname;
        } else if (/[/?#]/.test(s)) {
            const url = new URL(`https://${s.replace(/^\/+/, '')}`);
            host = url.hostname;
        } else {
            let hostPart = s.split('/')[0].split('?')[0].split('#')[0].trim();
            if (hostPart.startsWith('[')) {
                const end = hostPart.indexOf(']');
                host = end !== -1 ? hostPart.slice(1, end) : hostPart;
            } else if (hostPart.includes(':')) {
                host = hostPart.replace(/:(\d+)$/, '');
            } else {
                host = hostPart;
            }
        }
    } catch {
        return '';
    }

    if (!host) return '';
    return normalizeDomain(host, stripWww);
}

export function formatDuplicateDomainError(duplicateRows: Map<string, number[]>): string {
    const lines = ['以下のドメインが重複しています:'];
    const domains = Array.from(duplicateRows.keys()).sort();
    for (const domain of domains) {
        const rows = duplicateRows.get(domain) || [];
        const rowLabels = rows.map((row) => `行 ${row}`).join(', ');
        lines.push(`・${decodeDomainForDisplay(domain)}（${rowLabels}）`);
    }
    return lines.join('\n');
}

function trimHeader(value: unknown): string {
    if (value == null) return '';
    return String(value).trim().replace(/\u3000/g, ' ').trim();
}

type ColumnIndices = {
    asp: number;
    operation_company: number;
    media_name: number;
    domain: number;
    description: number;
};

function resolveColumnIndices(headerRow: unknown[]): ColumnIndices {
    const indices: Partial<ColumnIndices> = {};
    const found = new Set<string>();

    headerRow.forEach((cell, idx) => {
        const h = trimHeader(cell);
        if (!h) return;
        if (h === UNFIXABLE_COL_ASP) {
            indices.asp = idx;
            found.add(UNFIXABLE_COL_ASP);
        } else if (h === UNFIXABLE_COL_OPERATION_COMPANY) {
            indices.operation_company = idx;
            found.add(UNFIXABLE_COL_OPERATION_COMPANY);
        } else if (MEDIA_HEADER_ALIASES.has(h)) {
            indices.media_name = idx;
            found.add(UNFIXABLE_COL_MEDIA);
        } else if (DOMAIN_HEADER_ALIASES.has(h)) {
            indices.domain = idx;
            found.add(UNFIXABLE_COL_DOMAIN);
        } else if (h === UNFIXABLE_COL_DESCRIPTION) {
            indices.description = idx;
            found.add(UNFIXABLE_COL_DESCRIPTION);
        }
    });

    const missing = UNFIXABLE_REQUIRED_HEADERS.filter((label) => !found.has(label));
    if (missing.length > 0) {
        throw new UnfixableRulesParseError(
            `必須列が見つかりません: ${missing.join(', ')}。` +
                ` 列は ${UNFIXABLE_REQUIRED_HEADERS.join(', ')} が必要です。`
        );
    }

    return indices as ColumnIndices;
}

function cellAt(row: unknown[], idx: number): string {
    return cellStr(row[idx]);
}

export function parseUnfixableRulesExcel(arrayBuffer: ArrayBuffer): UnfixableDomainRuleRow[] {
    const wb = XLSX.read(arrayBuffer, { type: 'array' });
    const sheet = wb.Sheets[UNFIXABLE_RULES_SHEET_NAME];
    if (!sheet) {
        throw new UnfixableRulesParseError(
            `シート「${UNFIXABLE_RULES_SHEET_NAME}」が見つかりません。シート名を「${UNFIXABLE_RULES_SHEET_NAME}」にしてください。`
        );
    }

    const rows: unknown[][] = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
    if (!rows.length) {
        throw new UnfixableRulesParseError('ヘッダー行がありません。');
    }

    const col = resolveColumnIndices(rows[0] as unknown[]);

    const parsed: UnfixableDomainRuleRow[] = [];
    const domainRows = new Map<string, number[]>();

    for (let i = 1; i < rows.length; i++) {
        const excelRowNum = i + 1;
        const row = rows[i] as unknown[];
        const aspRaw = cellAt(row, col.asp);
        const operationCompanyRaw = cellAt(row, col.operation_company);
        const mediaNameRaw = cellAt(row, col.media_name);
        const domainRaw = cellAt(row, col.domain);
        const descriptionRaw = cellAt(row, col.description);

        if (
            !aspRaw &&
            !operationCompanyRaw &&
            !mediaNameRaw &&
            !domainRaw &&
            !descriptionRaw
        ) {
            continue;
        }

        if (!domainRaw) {
            throw new UnfixableRulesParseError(
                `行 ${excelRowNum}: 「${UNFIXABLE_COL_DOMAIN}」が空です。`,
                excelRowNum
            );
        }
        if (!descriptionRaw) {
            throw new UnfixableRulesParseError(
                `行 ${excelRowNum}: 「${UNFIXABLE_COL_DESCRIPTION}」が空です。`,
                excelRowNum
            );
        }

        const domain = domainInputToDomain(domainRaw);
        if (!domain) {
            throw new UnfixableRulesParseError(
                `行 ${excelRowNum}: 「${UNFIXABLE_COL_DOMAIN}」が無効です（URL またはドメイン形式で入力してください）。`,
                excelRowNum
            );
        }

        const existing = domainRows.get(domain) || [];
        existing.push(excelRowNum);
        domainRows.set(domain, existing);

        parsed.push({
            id: crypto.randomUUID(),
            domain,
            description: descriptionRaw,
            asp: aspRaw,
            operation_company: operationCompanyRaw,
            media_name: mediaNameRaw,
            sourceRow: excelRowNum,
        });
    }

    const duplicates = new Map<string, number[]>();
    domainRows.forEach((rowNums, domain) => {
        if (rowNums.length > 1) {
            duplicates.set(domain, rowNums);
        }
    });
    if (duplicates.size > 0) {
        throw new UnfixableRulesParseError(formatDuplicateDomainError(duplicates));
    }

    return sortUnfixableRulesBySourceRow(parsed);
}
